import type { Logger } from '@vp/ubik-logging'

import type { MerchantAvailabilityResponse, MerchandisingInventory, ProductInventory } from '../types/MerchantAvailability'
import { hasLowStock } from '../utils/InventoryHelpers'
import { getInventoryCount } from '../utils/MerchantAvailabilityHelpers'

const baseUrl = 'https://maise.products.cimpress.io'
const SIZE = 'Size'

export interface MerchantAvailabilityRequest {
  facetQuantities: MerchantAvailabilityFacet[]
}

interface MerchantAvailabilityFacet {
  value: string
  quantity: number
}

const assignInventoryLevel = (inventoryCount: number, minimumOrderQuantity: number) => {
  return hasLowStock(inventoryCount, minimumOrderQuantity) ? 'Low' : 'High'
}

export const getMerchantAvailability = async (
  merchant: string,
  productKey: string,
  productVersion: number,
  country: string,
  minimumOrderQuantity: number,
  selectedAttributes: Record<string, string>,
  isSizedGood: boolean,
  authorizationHeader: string,
  logger: Logger,
): Promise<MerchandisingInventory | undefined> => {
  try {
    if (!productKey || !productVersion) {
      throw Error('Missing product info')
    }

    const facetAttributeName = isSizedGood ? SIZE : undefined
    const searchParams = buildQueryParams(merchant, country, facetAttributeName, selectedAttributes)
    const url = `${baseUrl}/v3/product/${productKey}/versions/${productVersion}/availability?${searchParams.toString()}`
    const headers = { Authorization: authorizationHeader }
    const request = new Request(url, {
      headers,
    })

    const response = await fetch(request)

    if (!response.ok) {
      throw new Error(`Request to ${url} failed with status: ${response.status}`)
    }

    const data: MerchantAvailabilityResponse = await response.json()
    const facetCombinationMap = data.facetedAvailabilityStates.map((facet) => {
      const inventoryValue = getInventoryCount(facet.availabilityState)

      return {
        attributes: facet.attributeSelections,
        inventory: inventoryValue,
        inventoryLevel: assignInventoryLevel(inventoryValue, minimumOrderQuantity),
      } as ProductInventory
    })
    const totalInventory = getInventoryCount(data.availabilityState)

    return {
      combinationsInventories: facetCombinationMap,
      inventory: totalInventory,
      inventoryLevel: assignInventoryLevel(totalInventory, minimumOrderQuantity),
    }
  } catch (error) {
    logger.error('Cannot get product inventory for productKey', error as Error, { productKey, productVersion, country })
    return undefined
  }
}

export const getAvailabilityByFacet = async (
  merchant: string,
  productKey: string,
  productVersion: number,
  country: string,
  selectedAttributes: Record<string, string>,
  facet: string,
  authorizationHeader: string,
  logger: Logger,
): Promise<MerchantAvailabilityResponse | undefined> => {
  try {
    if (!productKey || !productVersion) {
      throw Error('Missing product info')
    }

    const searchParams = buildQueryParams(merchant, country, facet, selectedAttributes)
    const url = `${baseUrl}/v3/product/${productKey}/versions/${productVersion}/availability?${searchParams.toString()}`
    const headers = { Authorization: authorizationHeader }
    const request = new Request(url, {
      headers,
    })
    const response = await fetch(request)

    if (!response.ok) {
      throw new Error(`Request to ${url} failed with status: ${response.status}`)
    }

    const data = (await response.json()) as MerchantAvailabilityResponse
    return data
  } catch (error) {
    logger.error('Cannot get product inventory for productKey by facet', error as Error, { error: error, productKey, productVersion, country, facet })
    return undefined
  }
}

const buildQueryParams = (merchant: string, country: string, facetAttributeName: string | undefined, selectedAttributes: Record<string, string>) => {
  const params = {
    context: {
      Merchant: merchant,
      Country: country,
    },
    facetAttributeName,
    attributeSelections: selectedAttributes,
  }
  const searchParams = new URLSearchParams()

  if (params.context) {
    for (const [key, value] of Object.entries(params.context)) {
      searchParams.append(`context[${key}]`, String(value))
    }
  }

  if (params.facetAttributeName) {
    searchParams.append('facetAttributeName', String(params.facetAttributeName))
  }

  if (params.attributeSelections) {
    for (const [key, value] of Object.entries(params.attributeSelections)) {
      searchParams.append(`attributeSelections[${key}]`, String(value))
    }
  }

  return searchParams
}
